import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useUrlBackground} from "hooks/useBackgroundNumbers";
import {writeUrlBackground} from "libs/bgNumbering";
import {useQuery} from "@tanstack/react-query";
import {buildBgPresets} from "layout/BgTransformer/templateBg";
import {bakedBgStateForDefault, EMPTY_BAKED_BG_STATE} from "libs/bakedBg";
import {isScopesChannel, scopeRecordsFromDefault} from "libs/sceneScope";
import {useParams, useSearchParams} from "react-router-dom";
import {API} from "api";
import {TEXT_RECORD_FIELDS, useEdits} from "contexts/edits";
import {useImages} from "contexts/images";
import {INITIAL_SETTING_STATE, useSetting} from "contexts/setting";
import {findTypeObj} from "data/products";
import {drawnTemplates, materializeChannelDefaultVisibility} from "data/templateSlots";
import {useTemplateSelection} from "hooks/useTemplateSelection";
import {homeScope} from "data/offering";
import {DEFAULT_ROUTE_PATTERNS, useRouteParams} from "hooks/useRouteParams";
import {resolveAllOverrides} from "hooks/useDefaultSceneOverrides";
import {useAuth} from "hooks/useAuth";
import {useResolvedDefaultScene, useResolvedDefaultScenesAll} from "hooks/useResolvedDefaultScenes";
import {
  aggregateAllProductsFromScenes,
  materializeAllProductsWhatsNewFields,
} from "utils/allProductsScene";
import {prefersProductDefaultScene} from "libs/authoritativeDefaultScene";
import {withExtraTemplatesVisible} from "libs/bannerComposition";
import {resolveLegacySceneId} from "libs/legacySceneId";
import {defaultBgLook} from "libs/bgLook";
import {mergeScenes} from "utils/mergeScenes";
import {restoreLayeredCustomBgs} from "utils/restoreLayeredCustomBgs";
import {getAllProductsTemplateInstanceIds, getReleaseTemplateIdsForSet} from "utils/releases";

const TEXT_OBJECT_FIELDS = [...TEXT_RECORD_FIELDS, "gdnTagOrder", "tags"];
const BG_TRANSFORM_FIELDS = ["bgX", "bgY", "bgScale", "bgRotate", "bgFlip"];
const UPLOAD_TRANSFORM_FIELDS = [
  "uploadX",
  "uploadY",
  "uploadScale",
  "uploadRotate",
  "uploadFlip",
];

export function captureOwnPointCloudLayer(images, setting) {
  const ids = [...new Set([...Object.keys(setting.templateModes), ...setting.locked])]
    .filter(id => images.customBgs[id] &&
      (setting.templateModes[id]?.image === "own" || setting.locked.includes(id)));
  if (!ids.length) return null;
  const record = field => Object.fromEntries(ids.filter(id => images[field]?.[id] !== undefined).map(id => [id, images[field][id]]));
  return {
    customBgs: record("customBgs"),
    customBgUploaded: record("customBgUploaded"),
    bgPresets: record("bgPresets"),
    imageModes: Object.fromEntries(ids.filter(id => setting.templateModes[id]?.image === "own").map(id => [id, "own"])),
    locked: ids.filter(id => setting.locked.includes(id)),
    transforms: Object.fromEntries(BG_TRANSFORM_FIELDS.map(field => [field, record(field)])),
  };
}

function hasAnyScopedField(sceneData, fields) {
  if (!sceneData || typeof sceneData !== "object") return false;
  return fields.some((field) => {
    const value = sceneData[field];
    if (value === undefined || value === null) return false;
    if (Array.isArray(value)) return true;
    if (typeof value !== "object") return Boolean(value);
    return Object.keys(value).length > 0;
  });
}

function normalizeAllProductsTemplateList(list, allTemplateIds, fieldName) {
  if (!Array.isArray(list) || allTemplateIds.length === 0) return list;

  const idSet = new Set(allTemplateIds);
  const normalized = list.filter((id) => idSet.has(id));

  if (normalized.length === 0) {
    // Guard against stale defaults from regular releases templates (Splash, Screenshot, ...).
    // In ALL-products mode these IDs are incompatible and hide every product card.
    const hasForeignIds = list.some((id) => !idSet.has(id));
    if (hasForeignIds) {
      console.warn(`[useInitialScene] Ignoring incompatible ${fieldName} for ALL-products mode`, {
        value: list,
      });
      return allTemplateIds;
    }
    return list;
  }

  if (normalized.length !== list.length) {
    console.warn(`[useInitialScene] Dropping unknown ids from ${fieldName} in ALL-products mode`, {
      value: list,
    });
  }

  if (fieldName !== "order") {
    return normalized;
  }

  // Keep existing order for known ids and append missing templates to preserve a full sortable list.
  const normalizedSet = new Set(normalized);
  const missing = allTemplateIds.filter((id) => !normalizedSet.has(id));
  return [...normalized, ...missing];
}

function pickMostSpecificCompatibleArray(layers, field, allTemplateIds) {
  if (!Array.isArray(layers) || layers.length === 0) return undefined;
  if (!Array.isArray(allTemplateIds) || allTemplateIds.length === 0) return undefined;

  const idSet = new Set(allTemplateIds);

  for (let index = layers.length - 1; index >= 0; index -= 1) {
    const value = layers[index]?.[field];
    if (value === undefined) continue;
    if (!Array.isArray(value)) continue;
    if (value.length === 0) return value;
    if (value.every((id) => idSet.has(id))) return value;
  }

  return undefined;
}

/**
 * Legacy (v2) path for the ALL products view: run each product's own cascade
 * over the full override list, then fan the merged scenes out into scoped
 * "{TemplateId}-{ProductCode}" keys.
 *
 * Deliberately does NOT materialize the ALL-editor's per-product keys onto
 * canonical ones: the aggregate's job is the opposite direction (canonical →
 * scoped), and the ALL-editor's own values reach the merge through the global
 * layer. GET /api/v3/default-scenes/resolved-all resolves per product the same
 * way (it passes `materialize: false` for exactly this reason), so the flag-on
 * path feeds `aggregateAllProductsFromScenes` an equivalent scene map.
 */
/**
 * A row scoped to one background carries its OWN `bgOption`, redundant with
 * its key (every background-level save stamps it, back to the AIR import
 * scripts). `bgOption` is what PICKS which background-scoped row to read — a
 * background-scoped row can only ever hand back presets FOR that background,
 * never a vote on which one to pick. Left in, it wins the scalar "last
 * defined wins" merge over the real product/set-level choice, because it is
 * always the most specific layer. Surfaced 2026-10-11: reordering IJ's
 * gallery moved a stale background="1" row's bgOption="1" ahead of the new
 * product default. Mirrors omitSelfReferentialBgOption in
 * src/server/api/v3/resolve-defaults.ts (a separate copy — see its header).
 */
function sceneDataExcludingSelfBgOption(override) {
  const scene = override?.scene_data;
  if (!scene || !override.background || !("bgOption" in scene)) return scene;
  const {bgOption, ...rest} = scene;
  return rest;
}

function aggregateAllProductsFields(
  overrides,
  channel,
  type,
  set,
  layout,
  bg,
  productCodes,
  templateIds,
  extraTemplateIds
) {
  const scenesByProduct = {};
  for (const code of productCodes) {
    // Must use the same cascade as single-product route:
    // merge all matching scene_data layers (global -> product specific).
    // Otherwise ALL mode can diverge when a more-specific legacy hash row exists
    // without scene_data (it would shadow valid direct-field rows).
    const layers = resolveAllOverrides(overrides, channel, code, type, set, layout, bg)
      .map((o) => sceneDataExcludingSelfBgOption(o))
      .filter(Boolean);
    if (layers.length > 0) scenesByProduct[code] = mergeScenes(layers);
  }
  return aggregateAllProductsFromScenes(scenesByProduct, productCodes, templateIds, extraTemplateIds);
}

/**
 * Collapse the ALL-products layers (per-product aggregate first, override layers
 * on top) and then restore the template-list arrays from the most specific layer
 * that lists only ids valid in ALL mode.
 *
 * `layerScenes` are the raw override payloads (materialization never touches
 * visible/locked/order, so raw and materialized are interchangeable here).
 */
function mergeAllProductsScene(layers, layerScenes, allProductsTemplateIds) {
  const defined = layers.filter((l) => l && Object.keys(l).length > 0);
  if (defined.length === 0) return null;

  // Copy: with a single layer mergeScenes returns that object as-is, and the
  // array fields below would otherwise mutate a React Query cache entry.
  const merged = {...mergeScenes(defined)};
  for (const field of ["visible", "locked", "order"]) {
    const compatible = pickMostSpecificCompatibleArray(layerScenes, field, allProductsTemplateIds);
    if (compatible !== undefined) {
      merged[field] = compatible;
    }
  }
  return Object.keys(merged).length > 0 ? merged : null;
}

export function useInitialScene(channel, product, type, set, overridesState) {
  const {sceneId: savedSceneId} = useParams();
  // Which design of each slot the page draws (data/templateSlots.ts).
  const templateSelection = useTemplateSelection();
  const [searchParams, setSearchParams] = useSearchParams();
  const {featureFlags} = useAuth();
  // Phase-2 (issue #528, 2a): when on, the effective-transform selector derives
  // the bg crop (its resolved-scene layer reads the same default-scene cascade),
  // so this hook must not write bg transforms into the images context — that is
  // the third of the three racing writers. All OTHER scene fields (texts, logos,
  // uploads, options) still apply here unchanged.
  const bgDerivation = featureFlags?.bgDerivation === true && !savedSceneId;
  const overrides = overridesState?.overrides;
  const routeParams = useRouteParams(DEFAULT_ROUTE_PATTERNS);
  // The caller passes the product's home scope, so the route has to be read
  // through the same resolution before the two can be compared — a product
  // that carries its offering across channels (JetBrains Air) is under
  // /social/AIR/general and reads releases/release. See src/data/offering.ts.
  const routeScope = homeScope(
    routeParams?.channel,
    routeParams?.contentType,
    routeParams?.product
  );
  const isContextSyncedWithUrl =
    Boolean(savedSceneId) ||
    !routeParams?.channel ||
    (channel?.value === routeScope.channel &&
      product?.value === routeParams.product &&
      type?.value === routeScope.type);

  const typeObj = useMemo(() => {
    return findTypeObj(channel?.value, type?.value);
  }, [channel, type]);

  const products = useMemo(() => typeObj?.products ?? {}, [typeObj]);

  const urlLayoutRaw = searchParams.get("layout");
  // `?bg=<number>` or `?background=<identity>` (hooks/useBackgroundNumbers.ts).
  // A number waits for the numbered tiles: until App knows them, the page
  // does not know which background the URL names.
  const {background: urlBgRaw, isPending: isUrlBgPending} = useUrlBackground();
  const urlLayout = urlLayoutRaw || "1";
  const urlBg = urlBgRaw || "1";
  const urlHasLayout = urlLayoutRaw !== null;
  // The variant axis is URL-addressable like the layout, and a URL that names
  // it wins over the default scene the same way. It is also the fourth key
  // axis of a default-scene row — a treatment product picks its look as one
  // (layout, variant) cell — so the cascade below resolves against it.
  const urlVariantRaw = searchParams.get("variant");
  // Defaulted like `layout`: the setting context starts the treatment axis at
  // 1 too (contexts/setting.js), and the derived-crop selector resolves under
  // the context value — an unnamed variant must key the same cell in both.
  const urlVariant = urlVariantRaw || "1";
  const urlHasVariant = urlVariantRaw !== null;
  const urlHasBg = urlBgRaw !== null;
  // The language is URL-addressable like the layout, and a URL that names one
  // wins over the default scene the same way. Read when a default applies, by
  // the latest-ref pattern (see useScene): the Localization toggle writes the
  // param, and the legacy apply below has no dedup, so a dependency on it
  // would apply the default again and replace the user's text.
  const urlLocalizationRef = useRef(null);
  urlLocalizationRef.current = searchParams.get("localization");
  // What actually reaches the server's default-scene resolve: the real URL
  // value, or nothing at all — never the "1" bootstrap literal above. The
  // server cannot otherwise tell "the URL truly asks for background 1" apart
  // from "the URL has no background and this is `urlBg`'s own placeholder",
  // and the two must resolve differently: an explicit 1 is final, while no
  // background at all asks the server for the gallery's current default
  // (see default-scenes-resolved.ts's withConsistentCrop, 2026-10-12).
  const requestedBg = urlHasBg ? urlBg : "";

  const isAllProducts = product?.value === "ALL";

  // --- v3 default-scene hydration (issue #567, A1/A2) ---
  //
  // Flag on: the cascade runs on the server for THIS key only — one small
  // response replaces the channel-wide override list the client used to fetch
  // and resolve itself (GET /api/v3/default-scenes, ~200 KB). The response also
  // carries the matched `layers`, so the scoped-field checks below and the
  // ALL-products template-array pick keep working unchanged.
  //
  // The ALL-products view additionally needs every product's own resolution;
  // /resolved-all returns them in one response instead of re-running a
  // per-product cascade over the full list in the browser.
  const isV3Ready = Boolean(bgDerivation && channel?.value && product?.value && type?.value);
  const resolvedQuery = useResolvedDefaultScene({
    channel: channel?.value,
    product: product?.value,
    type: type?.value,
    set,
    layout: urlLayout,
    background: requestedBg,
    variant: urlVariant,
    enabled: isV3Ready && !isUrlBgPending,
  });
  const allProductsQuery = useResolvedDefaultScenesAll({
    channel: channel?.value,
    type: type?.value,
    set,
    layout: urlLayout,
    background: requestedBg,
    enabled: isV3Ready && isAllProducts && !isUrlBgPending,
  });

  // "Resolved" = the default-scene layer has settled (data or error), which
  // gates the legacy single-scene fetch and the apply effects. Flag off: the v2
  // override list. Flag on: the v3 queries (an error still counts as settled —
  // the route falls back to the legacy scene rather than hanging).
  const isV3Settled =
    (resolvedQuery.isSuccess || resolvedQuery.isError) &&
    (!isAllProducts || allProductsQuery.isSuccess || allProductsQuery.isError);
  // A tile number in the URL settles nothing until it names a background.
  const isOverridesResolved = isUrlBgPending
    ? false
    : bgDerivation
      ? !isV3Ready || isV3Settled
      : (overridesState?.isResolved ?? true);

  // --- Scene config from products.js (legacy fallback) ---
  const sceneConfig = useMemo(() => {
    if (!channel || !product || !type) return null;
    const defProd = Object.keys(products)?.[0];
    return products[product.value] || products[defProd];
  }, [channel, product, type, products]);

  // Legacy sceneId from products.js (used when no overrides exist).
  // Resolves set/layout/background-specific scenes from the products.js
  // defaults cascade before falling back to the top-level product sceneId.
  // The cascade itself lives in libs/legacySceneId — the server runs the SAME
  // function to serve this scene over v3 (issue #567 A1), and two copies would
  // be free to drift.
  const legacySceneId = useMemo(() => {
    if (!channel || !product || !type) return null;
    return resolveLegacySceneId({
      products,
      defaults: typeObj?.defaults,
      product: product.value,
      set,
      layout: urlLayout,
      background: urlBg,
    });
  }, [channel, product, type, typeObj, products, set, urlLayout, urlBg]);

  // --- Resolve all matching overrides and merge direct scene_data payloads ---
  const resolvedOverrides = useMemo(() => {
    if (!channel || !product || !type) return [];
    if (!isOverridesResolved) return [];
    // Flag on: the server ran the identical cascade and returned its layers.
    if (bgDerivation) return resolvedQuery.data?.layers ?? [];
    return resolveAllOverrides(
      overrides,
      channel.value,
      product.value,
      type.value,
      set,
      urlLayout,
      urlBg,
      urlVariant
    );
  }, [
    channel?.value,
    product?.value,
    type?.value,
    set,
    urlLayout,
    urlBg,
    urlVariant,
    overrides,
    isOverridesResolved,
    bgDerivation,
    resolvedQuery.data,
  ]);

  // Product codes for the current type (used to aggregate per-product fields for ALL view)
  const productCodes = useMemo(() => Object.keys(products), [products]);

  const overrideSceneData = useMemo(() => {
    const layerScenes = resolvedOverrides.map((o) => sceneDataExcludingSelfBgOption(o)).filter(Boolean);
    // The ALL view draws the banners its defaults add from other sets for
    // every product (libs/bannerComposition.ts), so their instances belong to
    // the ids its saved eyes and order are checked against, and each
    // product's own values for them are fanned out like the set's.
    const allProductsExtras = isAllProducts
      ? ((bgDerivation ? resolvedQuery.data?.scene_data : mergeScenes(layerScenes))
          ?.extraTemplates ?? [])
      : [];
    const allProductsTemplateIds = isAllProducts
      ? getAllProductsTemplateInstanceIds(productCodes, set, allProductsExtras, templateSelection)
      : [];
    const setTemplateIds = getReleaseTemplateIdsForSet(set, [], templateSelection);

    // --- Flag on: the merge already happened on the server ---
    if (bgDerivation) {
      const rawServerScene = resolvedQuery.data?.scene_data ?? null;
      // During a rolling deployment, an older resolver can omit Livestream's
      // customBgs from a multi-layer merge even though each raw layer contains
      // it. Rebuild only that missing field. The fixed resolver remains
      // authoritative as soon as it returns customBgs itself.
      const serverScene =
        channel?.value === "livestream"
          ? restoreLayeredCustomBgs(rawServerScene, layerScenes)
          : rawServerScene;
      if (isAllProducts) {
        const scenesByProduct = allProductsQuery.data?.scenes;
        const perProductScene =
          scenesByProduct && productCodes.length > 0
            ? aggregateAllProductsFromScenes(
                scenesByProduct,
                productCodes,
                setTemplateIds,
                allProductsExtras
              )
            : null;
        // mergeScenes([aggregate, serverMerged]) === mergeScenes([aggregate, ...layers]):
        // both object shallow-merge and last-defined-wins are associative over
        // the layer order, so pre-merging the override layers server-side is
        // equivalent to spreading them here.
        return mergeAllProductsScene(
          [perProductScene, serverScene],
          layerScenes,
          allProductsTemplateIds
        );
      }
      return serverScene && Object.keys(serverScene).length > 0 ? serverScene : null;
    }

    // --- Flag off: client-side cascade over the full override list ---
    if (resolvedOverrides.length === 0 && !isAllProducts) return null;

    const layers = layerScenes.map((scene) =>
      materializeAllProductsWhatsNewFields(
        materializeChannelDefaultVisibility(scene, channel?.value),
        product?.value
      )
    );

    // For ALL products view: aggregate per-product overrides (titles, features,
    // transforms, images, logos, etc.) and remap template IDs so each product's
    // WhatsNewPreview-{code} banner receives the correct data.
    // Per-product aggregation is the least-specific layer; global overrides win.
    if (isAllProducts && overrides?.length > 0 && productCodes.length > 0) {
      const perProductScene = aggregateAllProductsFields(
        overrides,
        channel?.value,
        type?.value,
        set,
        urlLayout,
        urlBg,
        productCodes,
        setTemplateIds,
        allProductsExtras
      );
      return mergeAllProductsScene(
        [perProductScene, ...layers],
        layerScenes,
        allProductsTemplateIds
      );
    }

    if (layers.length === 0) return null;
    const merged = mergeScenes(layers);
    return merged && Object.keys(merged).length > 0 ? merged : null;
  }, [
    resolvedOverrides,
    isAllProducts,
    product?.value,
    overrides,
    productCodes,
    channel?.value,
    type?.value,
    set,
    urlLayout,
    urlBg,
    bgDerivation,
    resolvedQuery.data,
    allProductsQuery.data,
    templateSelection,
  ]);

  // Derived flag: use override path when overrides resolved and produced data.
  //
  // The KED exception (Kotlin, Ktor, KMP, Compose Multiplatform, Exposed) lives
  // in libs/authoritativeDefaultScene — useEffectiveBgTransform makes the same
  // call for the derived crop, and a second copy here would be free to drift
  // (it did: #567).
  const isKEDPreferLegacy = useMemo(
    () =>
      prefersProductDefaultScene({
        product: product?.value,
        layers: resolvedOverrides,
        hasProductDefaultScene: Boolean(legacySceneId),
      }),
    [product?.value, resolvedOverrides, legacySceneId]
  );
  const useOverridePath = isOverridesResolved && overrideSceneData !== null && !isKEDPreferLegacy;

  const hasCurrentLayoutScopedTextDefaults = useMemo(() => {
    return resolvedOverrides.some(
      (entry) =>
        entry?.layout &&
        String(entry.layout) === urlLayout &&
        hasAnyScopedField(entry.scene_data, TEXT_OBJECT_FIELDS)
    );
  }, [resolvedOverrides, urlLayout]);

  const hasCurrentBgScopedTextDefaults = useMemo(() => {
    return resolvedOverrides.some(
      (entry) =>
        entry?.background &&
        String(entry.background) === urlBg &&
        hasAnyScopedField(entry.scene_data, TEXT_OBJECT_FIELDS)
    );
  }, [resolvedOverrides, urlBg]);

  const hasCurrentBgScopedTransforms = useMemo(() => {
    return resolvedOverrides.some(
      (entry) =>
        entry?.background &&
        String(entry.background) === urlBg &&
        hasAnyScopedField(entry.scene_data, BG_TRANSFORM_FIELDS)
    );
  }, [resolvedOverrides, urlBg]);

  // Primary scene ID
  const sceneId = useOverridePath
    ? `defaults:${channel?.value || ""}:${product?.value || ""}:${type?.value || ""}:${set || ""}:${urlLayout}:${urlBg}:${urlVariant}`
    : legacySceneId;

  const {loadTextFields, resetTextFields, editFontVariation} = useEdits();
  const imageContext = useImages();
  const {
    editLogoTransform,
    editLogos,
    loadLogos,
    loadLogoVariants,
    loadTextColorLocked,
    loadButtonColors,
    editLogoHidden,
    editTransform,
    editUploadTransform,
    editCustomBg,
    loadCustomBgs,
    loadBakedBgs,
    loadBgOpacities,
    loadBgBrightnesses,
    loadBgPresets,
    loadCustomBgUploaded,
    editBgBrightness,
    editBgBlurValue,
    editBgOpacity,
    loadImages,
    removeImages,
  } = imageContext;
  const settingContext = useSetting();
  const {
    bgOption,
    toggleUppercase,
    editBgOption,
    editLayoutOption,
    editVariantOption,
    editTemplateModes,
    editLayouts,
    editVariants,
    editBases,
    editDefaultTemplateValues,
    editFontOption,
    editIconOption,
    editIsFontToggle,
    editIsButtonEnabled,
    editGdnAccentColor,
    loadGdnColorVariants,
    editBase,
    editHash,
    loadVisible,
    loadLocked,
    editOrder,
    editHiddenTemplates,
    editExtraTemplates,
    extraTemplates: currentExtraTemplates,
    loadPixelation,
    loadDefaultSceneOptions,
    resetEpoch,
  } = settingContext;

  // The banners the channel draws, once per id: a page switch resets the eyes
  // and the order to them.
  const defaultTemplateIds = useMemo(
    () =>
      channel?.value
        ? [...new Set(drawnTemplates(channel.value, templateSelection).map(({id}) => id))]
        : [],
    [channel?.value, templateSelection]
  );

  // --- Legacy: the products.js default scene (when no overrides exist) ---
  //
  // Flag off this is its own `POST /scene/load/v2`. Flag on the scene arrives
  // with the v3 response as `product_default_scene` — the server ran
  // resolveLegacySceneId (the same function that produced `legacySceneId` above)
  // and loaded the hash server-side, so the page makes no v2 read at all, which
  // is issue #567's second acceptance criterion. The ~60 (channel, type, product)
  // keys with a products.js hash and no override row are the reason: without
  // this, every one of them still fell back to v2.
  const isLegacySceneWanted =
    isOverridesResolved &&
    !useOverridePath &&
    Boolean(legacySceneId) &&
    !savedSceneId &&
    isContextSyncedWithUrl;
  const {
    isSuccess: isSuccessLegacyFetch,
    isFetching: isFetchingLegacyFetch,
    isError: isErrorLegacyFetch,
    error: legacyFetchError,
    refetch: refetchLegacy,
    data: legacyFetchData,
  } = useQuery({
    queryKey: ["fetch-initial-scene", legacySceneId],
    queryFn: () => API.fetchScene(legacySceneId),
    enabled: !bgDerivation && isLegacySceneWanted,
    refetchOnWindowFocus: false,
    retry: false,
  });

  // Flag on the v3 query IS the legacy fetch. Gated on the same conditions as
  // that query, so a saved scene in the url or an unsynced context still means
  // "no legacy scene" rather than one the fetch would never have made. While the
  // v3 query is in flight `isOverridesResolved` is false (so nothing is served
  // yet), and a resolve failure surfaces through `overridesError` below — the
  // same channel the override half uses.
  const legacySceneData = bgDerivation
    ? isLegacySceneWanted
      ? (resolvedQuery.data?.product_default_scene ?? null)
      : undefined
    : legacyFetchData;
  // A 200 can still mean the scene-store read failed: the response carries the
  // cascade regardless, and reports the failure separately (see
  // default-scenes-resolved.ts). This is the only signal that a null
  // product_default_scene isn't "this key has no default" — and flag-on there's
  // no v2 fallback to cover it — so raise it as a load failure with a retry
  // instead of rendering the route's default content silently missing.
  const productDefaultError = bgDerivation
    ? (resolvedQuery.data?.product_default_error ?? null)
    : null;
  const isProductDefaultFailed = Boolean(isLegacySceneWanted && productDefaultError);
  const isSuccessLegacy = bgDerivation
    ? isLegacySceneWanted && !isProductDefaultFailed && legacySceneData !== null
    : isSuccessLegacyFetch;
  const isFetchingLegacy = bgDerivation ? false : isFetchingLegacyFetch;
  const isErrorLegacy = bgDerivation ? isProductDefaultFailed : isErrorLegacyFetch;
  const legacyError = useMemo(() => {
    if (!bgDerivation) return legacyFetchError;
    if (!isProductDefaultFailed) return null;
    return new Error(`Default scene couldn't be loaded: ${productDefaultError}`);
  }, [bgDerivation, legacyFetchError, isProductDefaultFailed, productDefaultError]);

  // --- Override path: direct payload flow from overrides ---
  const isSuccessOverrides = isOverridesResolved;
  // Flag off the override payload arrives with the list, so it can't fail on its
  // own. Flag on it's a fetch: surface the failure instead of silently rendering
  // a bannerless page (App's load-failure banner reads `error` below).
  const overridesError = bgDerivation
    ? (resolvedQuery.error ?? (isAllProducts ? allProductsQuery.error : null) ?? null)
    : null;
  const isErrorOverrides = overridesError !== null;
  const isFetchingOverrides = !isOverridesResolved;

  // Unified accessors
  const isSuccess = useOverridePath ? isSuccessOverrides : isSuccessLegacy;
  const isFetching = useOverridePath ? isFetchingOverrides : isFetchingLegacy;
  const initialSceneData = useOverridePath ? overrideSceneData : legacySceneData;

  // Whether the loaded scene included its own bg transforms (override path only)
  const [sceneHasBgTransforms, setSceneHasBgTransforms] = useState(false);
  // Becomes true only after initial scene data has been applied (override path only)
  const [isInitialSceneApplied, setIsInitialSceneApplied] = useState(false);
  // Ref-based dedup: tracks which scene_data object was last applied.
  // Unlike state, refs update immediately within the same render cycle,
  // so the apply effect can proceed without waiting for a re-render.
  const lastAppliedDataRef = useRef(null);
  const lastAppliedContextRef = useRef(null);
  const defaultContextKey = [channel?.value, product?.value, type?.value, set].join(":");
  const importLayerRef = useRef({images: imageContext, setting: settingContext});
  importLayerRef.current = {images: imageContext, setting: settingContext};
  // Keep own images only for an import on the same initialized route.
  function ownPointCloudLayer() {
    if (!lastAppliedDataRef.current || !searchParams.get("privateBackground") || urlBg !== "custom" ||
        lastAppliedContextRef.current !== defaultContextKey) return null;
    const {images, setting} = importLayerRef.current;
    return captureOwnPointCloudLayer(images, setting);
  }
  function importedImageModes(defaultModes, preserved) {
    const modes = {...defaultModes};
    for (const [id, image] of Object.entries(preserved?.imageModes || {})) modes[id] = {...modes[id], image};
    return modes;
  }


  // When true, the next override apply effect will load text fields from scene data.
  // Set to true on mount and after channel/product/type/set resets (which clear edits).
  // Set to false after the first application, so that layout/bg-only changes
  // re-apply visual settings without overwriting user-entered text.
  const shouldApplyTextFieldsRef = useRef(true);

  // Uploads belong to the working scene, not to a layout or background option.
  // Hydrate them once for a new channel/product/type/set context, then preserve
  // the user's image and cover-fit transform across visual-option changes.
  const shouldApplyUploadFieldsRef = useRef(true);

  // Track previous values to detect actual changes
  const prevChannelRef = useRef(channel?.value);
  const prevProductRef = useRef(product?.value);
  const prevTypeRef = useRef(type?.value);
  const prevSetRef = useRef(set);
  // A RESET of the setting context (resetOnArrival) clears what the reset
  // effects below fill in. On a route-key change they run anyway, but a RESET
  // can also arrive on the same key: the Home button on the default landing.
  const prevResetEpochRef = useRef(resetEpoch);
  const prevLayoutRef = useRef(urlLayout);
  const prevBgRef = useRef(urlBg);
  const prevLayoutHasScopedTextDefaultsRef = useRef(hasCurrentLayoutScopedTextDefaults);
  const prevBgHasScopedTextDefaultsRef = useRef(hasCurrentBgScopedTextDefaults);
  const prevSavedSceneIdRef = useRef(savedSceneId);

  // Route transition guard: when leaving /scene/:sceneId back to a route-default page,
  // force re-apply of defaults even if overrideSceneData reference is unchanged.
  useEffect(() => {
    const wasSceneRoute = Boolean(prevSavedSceneIdRef.current);
    const isSceneRoute = Boolean(savedSceneId);

    if (wasSceneRoute && !isSceneRoute) {
      lastAppliedDataRef.current = null;
      shouldApplyTextFieldsRef.current = true;
      shouldApplyUploadFieldsRef.current = true;
      setIsInitialSceneApplied(false);
      setSceneHasBgTransforms(false);
    }

    prevSavedSceneIdRef.current = savedSceneId;
  }, [savedSceneId]);

  /**
   * Give up the shared upload slot along with the upload the reset just wiped.
   *
   * `editCustomBg("")` empties the slot, but `bgOption` keeps pointing at it,
   * and `?background=custom` stays in the url — where `urlHasBg` then stops
   * the new context's default scene from choosing a background of its own.
   * The result is a background that exists nowhere: no image on any banner,
   * no tile selected in the gallery, and no way back except picking another
   * one by hand. Dropping the selection puts the user where someone who never
   * uploaded would be.
   */
  const releaseCustomBgSelection = useCallback(() => {
    if (bgOption !== "custom") return;
    editBgOption(INITIAL_SETTING_STATE.bgOption);
    setSearchParams(
      (params) => {
        writeUrlBackground(params, null, null);
        return params;
      },
      {replace: true}
    );
  }, [bgOption, editBgOption, setSearchParams]);

  // --- Legacy reset: dev behavior (no overrides) ---
  // Only reset texts/positions if sceneId exists; always reset images/logos/customBg
  useEffect(() => {
    if (useOverridePath) return;
    // While the default scene of the new route loads, the path of the route is
    // not known yet. This effect would record the new route as the current
    // one, and the override reset that follows the load would then see no
    // change: the default would apply without its texts.
    if (!isOverridesResolved) return;

    const channelChanged = prevChannelRef.current !== channel?.value;
    const productChanged = prevProductRef.current !== product?.value;
    const typeChanged = prevTypeRef.current !== type?.value;
    const wasReset = prevResetEpochRef.current !== resetEpoch;

    prevChannelRef.current = channel?.value;
    prevProductRef.current = product?.value;
    prevTypeRef.current = type?.value;
    prevResetEpochRef.current = resetEpoch;

    if ((channelChanged || productChanged || typeChanged || wasReset) && !savedSceneId) {
      shouldApplyUploadFieldsRef.current = true;
      if (sceneId) {
        resetTextFields();
        loadLocked([]);
        loadVisible(defaultTemplateIds);
        editOrder(defaultTemplateIds);
      }
      // The banners an admin removed or added belong to the page just left
      // (libs/bannerComposition.ts).
      editHiddenTemplates([]);
      editExtraTemplates([], {isAllProducts: false, productCodes: []});

      removeImages();
      editUploadTransform({}, {}, {}, {}, {});
      editLogos("main", "");
      loadLogoVariants({});
      loadTextColorLocked({});
      loadButtonColors({});
      loadGdnColorVariants({});
      editGdnAccentColor(null);
      editLogoHidden(false);
      editLogoTransform({}, {}, {});
      editCustomBg("");
      loadCustomBgs({});
      loadBakedBgs({}, null);
      loadCustomBgUploaded({});
      releaseCustomBgSelection();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    useOverridePath,
    isOverridesResolved,
    channel?.value,
    product?.value,
    type?.value,
    resetEpoch,
    savedSceneId,
    sceneId,
    defaultTemplateIds,
  ]);

  // --- Override reset: full reset on channel/product/type/set change ---
  useEffect(() => {
    if (!useOverridePath) return;

    const channelChanged = prevChannelRef.current !== channel?.value;
    const productChanged = prevProductRef.current !== product?.value;
    const typeChanged = prevTypeRef.current !== type?.value;
    const setChanged = prevSetRef.current !== set;
    const wasReset = prevResetEpochRef.current !== resetEpoch;

    prevChannelRef.current = channel?.value;
    prevProductRef.current = product?.value;
    prevTypeRef.current = type?.value;
    prevSetRef.current = set;
    prevResetEpochRef.current = resetEpoch;

    if ((channelChanged || productChanged || typeChanged || setChanged || wasReset) && !savedSceneId) {
      editHash("");
      setSceneHasBgTransforms(false);
      setIsInitialSceneApplied(false);
      lastAppliedDataRef.current = null;
      // Text fields were just cleared — the next apply effect must fill them from scene data.
      shouldApplyTextFieldsRef.current = true;
      shouldApplyUploadFieldsRef.current = true;
      resetTextFields();
      loadLocked([]);
      const isAllProductsMode = product?.value === "ALL";
      const allProductsIds = isAllProductsMode
        ? getAllProductsTemplateInstanceIds(productCodes, set, [], templateSelection)
        : [];
      const resetIds = allProductsIds.length > 0 ? allProductsIds : defaultTemplateIds;
      loadVisible(resetIds);
      editOrder(resetIds);
      // The banners an admin removed or added belong to the page just left
      // (libs/bannerComposition.ts).
      editHiddenTemplates([]);
      editExtraTemplates([], {isAllProducts: false, productCodes: []});
      removeImages();
      editUploadTransform({}, {}, {}, {}, {});
      editLogos("main", "");
      loadLogoVariants({});
      loadTextColorLocked({});
      loadButtonColors({});
      loadGdnColorVariants({});
      editGdnAccentColor(null);
      editLogoHidden(false);
      editLogoTransform({}, {}, {});
      editCustomBg("");
      loadCustomBgs({});
      loadBakedBgs({}, null);
      loadCustomBgUploaded({});
      releaseCustomBgSelection();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    useOverridePath,
    channel?.value,
    product?.value,
    type?.value,
    set,
    resetEpoch,
    savedSceneId,
    defaultTemplateIds,
    productCodes,
  ]);

  // Re-apply texts when switching between layout/background-scoped defaults.
  // This preserves "do not overwrite on visual-only changes" while still allowing
  // partial text defaults saved at level=layout or level=background to load.
  useEffect(() => {
    if (!useOverridePath) return;

    const layoutChanged = prevLayoutRef.current !== urlLayout;
    const bgChanged = prevBgRef.current !== urlBg;

    const shouldApplyLayoutScopedTexts =
      layoutChanged &&
      (hasCurrentLayoutScopedTextDefaults || prevLayoutHasScopedTextDefaultsRef.current);
    const shouldApplyBgScopedTexts =
      bgChanged && (hasCurrentBgScopedTextDefaults || prevBgHasScopedTextDefaultsRef.current);

    if (shouldApplyLayoutScopedTexts || shouldApplyBgScopedTexts) {
      shouldApplyTextFieldsRef.current = true;
    }

    prevLayoutRef.current = urlLayout;
    prevBgRef.current = urlBg;
    prevLayoutHasScopedTextDefaultsRef.current = hasCurrentLayoutScopedTextDefaults;
    prevBgHasScopedTextDefaultsRef.current = hasCurrentBgScopedTextDefaults;
  }, [
    useOverridePath,
    urlLayout,
    urlBg,
    hasCurrentLayoutScopedTextDefaults,
    hasCurrentBgScopedTextDefaults,
  ]);

  // No separate re-apply effects needed for override path: the ref-based dedup
  // in the apply effect handles both product switches (new initialSceneData
  // reference) and in-place data updates (new overrideSceneData reference).

  // --- Legacy apply: dev behavior (no isInitialSceneApplied guard) ---
  useEffect(() => {
    if (useOverridePath) return;
    if (!(isSuccess && !isFetching && !savedSceneId && sceneId && isContextSyncedWithUrl)) return;
    const preservedImport = ownPointCloudLayer();

    editHash(sceneId);

    loadTextFields(initialSceneData);
    // Logos
    if (initialSceneData.logos) {
      loadLogos(initialSceneData.logos);
    }
    loadLogoVariants(initialSceneData.logoVariants);
    loadTextColorLocked(initialSceneData.textColorLocked);
    // The defaults of the route set every button color. A background without
    // them shows the default look, not the colors of the previous background.
    loadButtonColors(initialSceneData.buttonColors);
    if (initialSceneData.logoHidden !== undefined) {
      editLogoHidden(!!initialSceneData.logoHidden);
    }
    if (initialSceneData.logos?.main && products[product.value]) {
      editLogos("main", initialSceneData.logos.main);
    } else {
      editLogos("main", "");
    }
    if (initialSceneData.logoX || initialSceneData.logoY || initialSceneData.logoScale) {
      editLogoTransform(initialSceneData.logoX, initialSceneData.logoY, initialSceneData.logoScale);
    }

    // Background transforms and uploads
    if (
      !bgDerivation &&
      (initialSceneData.bgY ||
        initialSceneData.bgX ||
        initialSceneData.bgScale ||
        initialSceneData.bgRotate ||
        initialSceneData.bgFlip)
    ) {
      editTransform(
        preservedImport ? {...initialSceneData.bgX, ...preservedImport.transforms.bgX} : initialSceneData.bgX,
        preservedImport ? {...initialSceneData.bgY, ...preservedImport.transforms.bgY} : initialSceneData.bgY,
        preservedImport ? {...initialSceneData.bgScale, ...preservedImport.transforms.bgScale} : initialSceneData.bgScale,
        preservedImport ? {...initialSceneData.bgRotate, ...preservedImport.transforms.bgRotate} : initialSceneData.bgRotate,
        preservedImport ? {...initialSceneData.bgFlip, ...preservedImport.transforms.bgFlip} : initialSceneData.bgFlip
      );
    }
    if (shouldApplyUploadFieldsRef.current) {
      if (hasAnyScopedField(initialSceneData, UPLOAD_TRANSFORM_FIELDS)) {
        editUploadTransform(
          initialSceneData.uploadX,
          initialSceneData.uploadY,
          initialSceneData.uploadScale,
          initialSceneData.uploadRotate,
          initialSceneData.uploadFlip
        );
      }
      if (sceneConfig?.uploadImages && Object.keys(sceneConfig.uploadImages).length > 0) {
        loadImages(sceneConfig.uploadImages);
      } else if (hasAnyScopedField(initialSceneData, ["uploadImages"])) {
        loadImages(initialSceneData.uploadImages);
      }
      shouldApplyUploadFieldsRef.current = false;
    }
    if (initialSceneData.customBg) {
      editCustomBg(initialSceneData.customBg);
    }
    if (initialSceneData.customBgs !== undefined) {
      loadCustomBgs(preservedImport ? {...initialSceneData.customBgs, ...preservedImport.customBgs} : initialSceneData.customBgs);
    }
    // A products.js scene has no baked backgrounds (libs/bakedBg.ts).
    loadBakedBgs({}, null);
    // Per-template presets (issue: "position one banner's background and save
    // it as the default"). Loaded whole, and snapshotted into bgPresets so
    // re-attaching a template can put them back — see templateBg.ts.
    loadBgOpacities(initialSceneData.bgOpacities);
    loadBgBrightnesses(initialSceneData.bgBrightnesses);
    loadBgPresets({...buildBgPresets(initialSceneData), ...preservedImport?.bgPresets});
    // Scene scopes (libs/sceneScope.ts): every per-template value a default
    // carries is a preset — it shows with the chain intact and yields to the
    // next shared change — and the snapshot is what "reset to default" uses.
    if (isScopesChannel(channel?.value)) {
      const records = scopeRecordsFromDefault(initialSceneData);
      editLayouts(records.layouts);
      editVariants(records.variants);
      editBases(records.bases);
      editTemplateModes(importedImageModes(records.templateModes, preservedImport));
      editDefaultTemplateValues(records.defaultTemplateValues);
    } else {
      // Per-template base colour (IJ Figma backgrounds, COART-…): layouts,
      // variants, and template modes stay chain-based outside Releases, but a
      // default scene's own per-template `bases` must still show — a vertical
      // banner's background can carry a different accent than a square one's.
      editBases(
        initialSceneData.bases && typeof initialSceneData.bases === "object" ? initialSceneData.bases : {}
      );
    }
    if (initialSceneData.bgBrightness !== undefined) {
      editBgBrightness(initialSceneData.bgBrightness);
    }
    if (initialSceneData.bgBlurValue !== undefined) {
      editBgBlurValue(initialSceneData.bgBlurValue);
    }
    if (initialSceneData.bgOpacity !== undefined) {
      editBgOpacity(initialSceneData.bgOpacity);
    }

    // Pixelation overlay (gate + knobs). Default scenes don't go through the
    // LOAD_SCENE reducer, so apply the pixelation subset here — nothing else
    // does, and since no channel defaults the overlay on, an explicit
    // `pixelationEnabled: true` here is the only thing that turns it on.
    // "Present wins" (see LOAD_PIXELATION). COART-1465.
    loadPixelation(initialSceneData);
    // Title size and the generative background ("present wins").
    loadDefaultSceneOptions(initialSceneData);

    // Setting options — don't override URL-specified values with scene defaults.
    // Parallel branch at ~line 1099 already guards these; the sceneConfig path
    // here used to miss the guard, causing ?layout=1 to briefly win before the
    // sceneConfig default (e.g. layout=2) overwrote it on fresh load.
    if (initialSceneData.bgOption && !urlHasBg) editBgOption(initialSceneData.bgOption);
    if ((initialSceneData.layoutOption || sceneConfig?.layout) && !urlHasLayout) {
      editLayoutOption(+(sceneConfig?.layout || initialSceneData.layoutOption));
    }
    if (initialSceneData.variantOption && !urlHasVariant) {
      editVariantOption(+initialSceneData.variantOption);
    }
    if (initialSceneData.fontOption && !urlLocalizationRef.current) {
      editFontOption(initialSceneData.fontOption);
    }
    // iconOption is a string ("key"/"book"/etc.); see #168 — pre-refactor
    // setting.js cast it to Number, producing NaN for word-keyed icons.
    if (initialSceneData.iconOption) editIconOption(initialSceneData.iconOption);
    if (initialSceneData.isFontToggle) editIsFontToggle(initialSceneData.isFontToggle);
    if (initialSceneData.isButtonEnabled !== undefined) {
      editIsButtonEnabled(initialSceneData.isButtonEnabled);
    }
    if (initialSceneData.gdnColorVariants !== undefined) {
      loadGdnColorVariants(initialSceneData.gdnColorVariants);
    }
    if (initialSceneData.gdnAccentColor !== undefined) {
      editGdnAccentColor(initialSceneData.gdnAccentColor);
    }
    if (initialSceneData.base) editBase(initialSceneData.base);
    if (initialSceneData.fontVariation !== undefined) {
      editFontVariation(initialSceneData.fontVariation);
    }

    // The banners a default adds join the eyes and the order, and they stay
    // on: the eyes it saved can be older than them (libs/bannerComposition.ts).
    const bannerListScope = {isAllProducts: product?.value === "ALL", productCodes};
    if (initialSceneData.extraTemplates !== undefined) {
      editExtraTemplates(initialSceneData.extraTemplates, bannerListScope);
    }
    if (initialSceneData.hiddenTemplates !== undefined) {
      editHiddenTemplates(initialSceneData.hiddenTemplates);
    }
    if (initialSceneData.visible !== undefined) {
      loadVisible(
        withExtraTemplatesVisible(
          initialSceneData.visible,
          initialSceneData.extraTemplates,
          bannerListScope
        )
      );
    }
    if (initialSceneData.locked !== undefined) {
      loadLocked(preservedImport ? [...new Set([...initialSceneData.locked, ...preservedImport.locked])] : initialSceneData.locked);
    }
    if (initialSceneData.order !== undefined) {
      editOrder(initialSceneData.order);
    }

    if (initialSceneData.isUppercase !== undefined) {
      toggleUppercase(initialSceneData.isUppercase);
    }
    lastAppliedDataRef.current = initialSceneData;
    lastAppliedContextRef.current = defaultContextKey;
  }, [
    useOverridePath,
    editLogos,
    editLogoTransform,
    loadTextFields,
    editFontVariation,
    loadLogos,
    loadLogoVariants,
    loadTextColorLocked,
    loadButtonColors,
    loadGdnColorVariants,
    editGdnAccentColor,
    editLogoHidden,
    editTransform,
    editUploadTransform,
    editCustomBg,
    loadCustomBgs,
    loadBakedBgs,
    loadCustomBgUploaded,
    loadBgOpacities,
    loadBgBrightnesses,
    loadBgPresets,
    editBgBrightness,
    editBgBlurValue,
    editBgOpacity,
    loadPixelation,
    loadDefaultSceneOptions,
    loadImages,
    editBgOption,
    editLayoutOption,
    editVariantOption,
    editTemplateModes,
    editLayouts,
    editVariants,
    editBases,
    editDefaultTemplateValues,
    editFontOption,
    editIconOption,
    editIsFontToggle,
    editIsButtonEnabled,
    editBase,
    editHash,
    toggleUppercase,
    loadVisible,
    editHiddenTemplates,
    editExtraTemplates,
    loadLocked,
    editOrder,
    isFetching,
    isSuccess,
    initialSceneData,
    products,
    product,
    isContextSyncedWithUrl,
    savedSceneId,
    sceneConfig,
    sceneId,
    urlHasBg,
    urlHasLayout,
    urlHasVariant,
    bgDerivation,
    // Applies again after a RESET on the same route key (see prevResetEpochRef).
    resetEpoch,
  ]);

  // --- Override apply: guarded, with sceneHasBgTransforms tracking ---
  // Uses ref-based dedup instead of isInitialSceneApplied state to avoid the
  // one-render-late problem: when product changes, the reset effect sets
  // isInitialSceneApplied=false but state updates don't take effect until the
  // NEXT render. By checking a ref (which mutates immediately), the apply
  // effect can proceed in the same render cycle as the reset.
  useEffect(() => {
    if (!useOverridePath) return;
    if (!(isSuccess && !isFetching && !savedSceneId && sceneId && isContextSyncedWithUrl)) return;

    if (!initialSceneData) {
      if (lastAppliedDataRef.current !== null) {
        setSceneHasBgTransforms(false);
        setIsInitialSceneApplied(true);
        lastAppliedDataRef.current = null;
      }
      return;
    }

    // Skip if this exact scene data object was already applied.
    if (lastAppliedDataRef.current === initialSceneData) {
      return;
    }

    const preservedImport = ownPointCloudLayer();
    editHash(sceneId);

    // Only load text fields on the first application after a channel/product/type/set
    // change (when the reset effect cleared them). Layout-only or bg-only changes
    // must NOT overwrite user-entered text — only visual/layout settings are re-applied.
    if (shouldApplyTextFieldsRef.current) {
      loadTextFields(initialSceneData);
      shouldApplyTextFieldsRef.current = false;
    }

    // Logos
    if (initialSceneData.logos) {
      loadLogos(initialSceneData.logos);
    }
    loadLogoVariants(initialSceneData.logoVariants);
    loadTextColorLocked(initialSceneData.textColorLocked);
    // The defaults of the route set every button color. A background without
    // them shows the default look, not the colors of the previous background.
    loadButtonColors(initialSceneData.buttonColors);
    if (initialSceneData.logoHidden !== undefined) {
      editLogoHidden(!!initialSceneData.logoHidden);
    }
    // The cascade is resolved for this product, so its shared logo is this
    // product's. The legacy apply above also needs a products.js scene of the
    // product: without one the scene is another product's. The ALL view draws
    // every product, and one uploaded logo is no product's.
    editLogos("main", (!isAllProducts && initialSceneData.logos?.main) || "");
    if (initialSceneData.logoX || initialSceneData.logoY || initialSceneData.logoScale) {
      editLogoTransform(initialSceneData.logoX, initialSceneData.logoY, initialSceneData.logoScale);
    }

    // Background transforms and uploads
    const hasBgTransforms =
      initialSceneData.bgY ||
      initialSceneData.bgX ||
      initialSceneData.bgScale ||
      initialSceneData.bgRotate ||
      initialSceneData.bgFlip;
    const sceneBg = initialSceneData.bgOption ? String(initialSceneData.bgOption) : null;
    const urlOverridesToDifferentBg = urlHasBg && sceneBg && urlBg !== sceneBg;
    const shouldApplySceneBgTransforms =
      Boolean(hasBgTransforms) && (!urlOverridesToDifferentBg || hasCurrentBgScopedTransforms);
    // Under bgDerivation the selector owns the crop (its resolved-scene layer
    // reads this same merged scene) — skip the context write, but still report
    // sceneHasBgTransforms below so unrelated consumers keep their signal.
    if (!bgDerivation && shouldApplySceneBgTransforms) {
      editTransform(
        preservedImport ? {...initialSceneData.bgX, ...preservedImport.transforms.bgX} : initialSceneData.bgX,
        preservedImport ? {...initialSceneData.bgY, ...preservedImport.transforms.bgY} : initialSceneData.bgY,
        preservedImport ? {...initialSceneData.bgScale, ...preservedImport.transforms.bgScale} : initialSceneData.bgScale,
        preservedImport ? {...initialSceneData.bgRotate, ...preservedImport.transforms.bgRotate} : initialSceneData.bgRotate,
        preservedImport ? {...initialSceneData.bgFlip, ...preservedImport.transforms.bgFlip} : initialSceneData.bgFlip
      );
    }
    setSceneHasBgTransforms(shouldApplySceneBgTransforms);
    if (shouldApplyUploadFieldsRef.current) {
      if (hasAnyScopedField(initialSceneData, UPLOAD_TRANSFORM_FIELDS)) {
        editUploadTransform(
          initialSceneData.uploadX,
          initialSceneData.uploadY,
          initialSceneData.uploadScale,
          initialSceneData.uploadRotate,
          initialSceneData.uploadFlip
        );
      }
      if (hasAnyScopedField(initialSceneData, ["uploadImages"])) {
        loadImages(initialSceneData.uploadImages);
      }
      shouldApplyUploadFieldsRef.current = false;
    }
    if (initialSceneData.customBg) {
      editCustomBg(initialSceneData.customBg);
    }
    if (initialSceneData.customBgs !== undefined) {
      loadCustomBgs(preservedImport ? {...initialSceneData.customBgs, ...preservedImport.customBgs} : initialSceneData.customBgs);
    }
    // Baked backgrounds (libs/bakedBg.ts): only for the background this
    // cascade was merged for. On the server that is the scene's own bgOption;
    // the client-side cascade merges for the URL background (or its "1"
    // placeholder). The ALL-products view keys banners by product instance and
    // keeps the crop.
    const bakedState = isAllProducts
      ? EMPTY_BAKED_BG_STATE
      : bakedBgStateForDefault({
          scene: initialSceneData,
          resolvedFor: bgDerivation ? initialSceneData.bgOption : urlBg,
          displayed: urlHasBg ? urlBg : initialSceneData.bgOption,
        });
    loadBakedBgs(bakedState.bakedBgs, bakedState.option);
    // Per-template presets (issue: "position one banner's background and save
    // it as the default"). Loaded whole, and snapshotted into bgPresets so
    // re-attaching a template can put them back — see templateBg.ts.
    loadBgOpacities(initialSceneData.bgOpacities);
    loadBgBrightnesses(initialSceneData.bgBrightnesses);
    loadBgPresets({...buildBgPresets(initialSceneData), ...preservedImport?.bgPresets});
    // Scene scopes (libs/sceneScope.ts): every per-template value a default
    // carries is a preset — it shows with the chain intact and yields to the
    // next shared change — and the snapshot is what "reset to default" uses.
    if (isScopesChannel(channel?.value)) {
      const records = scopeRecordsFromDefault(initialSceneData);
      editLayouts(records.layouts);
      editVariants(records.variants);
      editBases(records.bases);
      editTemplateModes(importedImageModes(records.templateModes, preservedImport));
      editDefaultTemplateValues(records.defaultTemplateValues);
    } else {
      // Per-template base colour (IJ Figma backgrounds, COART-…): layouts,
      // variants, and template modes stay chain-based outside Releases, but a
      // default scene's own per-template `bases` must still show — a vertical
      // banner's background can carry a different accent than a square one's.
      editBases(
        initialSceneData.bases && typeof initialSceneData.bases === "object" ? initialSceneData.bases : {}
      );
    }
    // Each background has its own look (libs/bgLook.ts). A default with no
    // look gives the neutral one, not the look of the background before it.
    const look = defaultBgLook(initialSceneData);
    editBgBrightness(look.bgBrightness);
    if (initialSceneData.bgBlurValue !== undefined) {
      editBgBlurValue(initialSceneData.bgBlurValue);
    }
    editBgOpacity(look.bgOpacity);

    // Pixelation overlay (gate + knobs). Default scenes don't go through the
    // LOAD_SCENE reducer, so apply the pixelation subset here — nothing else
    // does, and since no channel defaults the overlay on, an explicit
    // `pixelationEnabled: true` here is the only thing that turns it on.
    // "Present wins" (see LOAD_PIXELATION). COART-1465.
    loadPixelation(initialSceneData);
    // Title size and the generative background ("present wins").
    loadDefaultSceneOptions(initialSceneData);

    // Setting options — don't override URL-specified values with merged scene data
    if (initialSceneData.bgOption && !urlHasBg) editBgOption(initialSceneData.bgOption);
    if (initialSceneData.layoutOption && !urlHasLayout) {
      editLayoutOption(+initialSceneData.layoutOption);
    }
    if (initialSceneData.variantOption && !urlHasVariant) {
      editVariantOption(+initialSceneData.variantOption);
    }
    if (initialSceneData.fontOption && !urlLocalizationRef.current) {
      editFontOption(initialSceneData.fontOption);
    }
    // iconOption is a string ("key"/"book"/etc.); see #168 — pre-refactor
    // setting.js cast it to Number, producing NaN for word-keyed icons.
    if (initialSceneData.iconOption) editIconOption(initialSceneData.iconOption);
    if (initialSceneData.isFontToggle) editIsFontToggle(initialSceneData.isFontToggle);
    if (initialSceneData.isButtonEnabled !== undefined) {
      editIsButtonEnabled(initialSceneData.isButtonEnabled);
    }
    if (initialSceneData.gdnColorVariants !== undefined) {
      loadGdnColorVariants(initialSceneData.gdnColorVariants);
    }
    if (initialSceneData.gdnAccentColor !== undefined) {
      editGdnAccentColor(initialSceneData.gdnAccentColor);
    }
    if (initialSceneData.base) editBase(initialSceneData.base);
    if (initialSceneData.fontVariation !== undefined) {
      editFontVariation(initialSceneData.fontVariation);
    }

    const isAllProductsMode = product?.value === "ALL";
    // The banners this default adds from other sets (libs/bannerComposition.ts)
    // are instances of the ALL view too, so its saved eyes and order keep them.
    const sceneExtraTemplates = initialSceneData.extraTemplates ?? currentExtraTemplates;
    const allProductsTemplateIds = isAllProductsMode
      ? getAllProductsTemplateInstanceIds(productCodes, set, sceneExtraTemplates, templateSelection)
      : [];

    // The banners a default adds join the eyes and the order, and they stay
    // on: the eyes it saved can be older than them.
    const bannerListScope = {isAllProducts: isAllProductsMode, productCodes};
    if (initialSceneData.extraTemplates !== undefined) {
      editExtraTemplates(initialSceneData.extraTemplates, bannerListScope);
    }
    if (initialSceneData.hiddenTemplates !== undefined) {
      editHiddenTemplates(initialSceneData.hiddenTemplates);
    }

    if (initialSceneData.visible !== undefined) {
      const normalizedVisible = isAllProductsMode
        ? normalizeAllProductsTemplateList(
            initialSceneData.visible,
            allProductsTemplateIds,
            "visibility"
          )
        : initialSceneData.visible;
      loadVisible(withExtraTemplatesVisible(normalizedVisible, sceneExtraTemplates, bannerListScope));
    }
    if (initialSceneData.locked !== undefined) {
      loadLocked(preservedImport ? [...new Set([...initialSceneData.locked, ...preservedImport.locked])] : initialSceneData.locked);
    }
    if (initialSceneData.order !== undefined) {
      const normalizedOrder = isAllProductsMode
        ? normalizeAllProductsTemplateList(initialSceneData.order, allProductsTemplateIds, "order")
        : initialSceneData.order;
      editOrder(normalizedOrder);
    }

    if (initialSceneData.isUppercase !== undefined) {
      toggleUppercase(initialSceneData.isUppercase);
    }

    lastAppliedDataRef.current = initialSceneData;
    lastAppliedContextRef.current = defaultContextKey;
    setIsInitialSceneApplied(true);
  }, [
    useOverridePath,
    editLogos,
    editLogoTransform,
    loadTextFields,
    editFontVariation,
    loadLogos,
    loadLogoVariants,
    loadTextColorLocked,
    loadButtonColors,
    loadGdnColorVariants,
    editGdnAccentColor,
    editLogoHidden,
    editTransform,
    editUploadTransform,
    editCustomBg,
    loadCustomBgs,
    loadBakedBgs,
    loadCustomBgUploaded,
    loadBgOpacities,
    loadBgBrightnesses,
    loadBgPresets,
    editBgBrightness,
    editBgBlurValue,
    editBgOpacity,
    loadPixelation,
    loadDefaultSceneOptions,
    loadImages,
    editBgOption,
    editLayoutOption,
    editVariantOption,
    editTemplateModes,
    editLayouts,
    editVariants,
    editBases,
    editDefaultTemplateValues,
    editFontOption,
    editIconOption,
    editIsFontToggle,
    editIsButtonEnabled,
    editBase,
    editHash,
    toggleUppercase,
    loadVisible,
    editHiddenTemplates,
    editExtraTemplates,
    loadLocked,
    editOrder,
    isFetching,
    isSuccess,
    initialSceneData,
    product,
    isContextSyncedWithUrl,
    savedSceneId,
    sceneId,
    urlBg,
    urlHasBg,
    urlHasLayout,
    urlHasVariant,
    hasCurrentBgScopedTransforms,
    set,
    productCodes,
    bgDerivation,
    isAllProducts,
    // The reset effect clears lastAppliedDataRef, and this applies again
    // after a RESET on the same route key (see prevResetEpochRef).
    resetEpoch,
  ]);

  // A background with no default has no sceneId, so neither apply effect runs.
  // Gallery selection replaces its images. Clear the previous default's look
  // and presets too, so a second click cannot restore the old background cuts.
  useEffect(() => {
    if (
      savedSceneId ||
      sceneId ||
      !isContextSyncedWithUrl ||
      !isOverridesResolved ||
      isFetching ||
      isErrorOverrides ||
      isErrorLegacy ||
      !lastAppliedDataRef.current
    ) return;

    loadLogoVariants({});
    loadTextColorLocked({});
    loadButtonColors({});
    loadBgPresets({});
    loadBgOpacities({});
    loadBgBrightnesses({});
    loadBakedBgs({}, null);
    editBases({});
    const look = defaultBgLook(null);
    editBgBrightness(look.bgBrightness);
    editBgOpacity(look.bgOpacity);
    lastAppliedDataRef.current = null;
    setSceneHasBgTransforms(false);
    setIsInitialSceneApplied(false);
  }, [
    savedSceneId,
    sceneId,
    isContextSyncedWithUrl,
    isOverridesResolved,
    isFetching,
    isErrorOverrides,
    isErrorLegacy,
    loadLogoVariants,
    loadTextColorLocked,
    loadButtonColors,
    loadBgPresets,
    loadBgOpacities,
    loadBgBrightnesses,
    loadBakedBgs,
    editBases,
    editBgBrightness,
    editBgOpacity,
  ]);

  // --- Return values ---
  // Legacy: dev-style simple return
  // Override path: full return with sceneHasBgTransforms
  const isInitialSceneLoaded = useOverridePath
    ? Boolean(savedSceneId) ||
      (isContextSyncedWithUrl &&
        isOverridesResolved &&
        (overrideSceneData === null || isErrorOverrides || isInitialSceneApplied))
    : isSuccess && !isFetching;

  // Whether a route-default scene exists (override or legacy) for this route
  const hasRouteDefaultScene = useOverridePath || legacySceneId !== null;

  // The route's default scene is known: it is applied, the route has none, or
  // it failed to load. Before the resolve answers, `hasRouteDefaultScene` can
  // read false on a route whose default exists (a route with no products.js
  // scene, such as email), so it cannot tell "no default" from "not known yet".
  // App holds the backgrounds of a baking channel until this is true.
  const isDefaultSceneSettled =
    Boolean(savedSceneId) ||
    (isContextSyncedWithUrl &&
      isOverridesResolved &&
      (!hasRouteDefaultScene ||
        isInitialSceneLoaded ||
        isErrorOverrides ||
        (!useOverridePath && isErrorLegacy)));

  // A missing legacy scene is not a terminal state until the resolver settles.
  // A fetched default must finish applying before a later import can use it.
  const isInitialSceneReady = Boolean(savedSceneId) ||
    (isContextSyncedWithUrl && isOverridesResolved && !isFetching &&
      !isErrorOverrides && !isErrorLegacy &&
      (!hasRouteDefaultScene ||
        (isInitialSceneLoaded && initialSceneData &&
          lastAppliedDataRef.current === initialSceneData)));


  // Whether the initial scene data carries its own bg transform fields
  const hasInitialSceneTransforms = sceneHasBgTransforms;

  // The bg option stored in the initial scene data (so useBackgrounds knows which bg
  // the transforms belong to and can skip overwriting them)
  const initialSceneBgOption = initialSceneData?.bgOption ?? null;

  // Retry whichever layer actually failed. Under the flag that includes the v3
  // default-scene queries, which replaced the (App-level) override list as the
  // thing that can fail while loading a route default.
  const retry = useCallback(() => {
    // isProductDefaultFailed is a 200 whose scene-store half failed, so the query
    // itself isn't in an error state — refetching it is the only way to retry.
    if (resolvedQuery.isError || isProductDefaultFailed) resolvedQuery.refetch();
    if (allProductsQuery.isError) allProductsQuery.refetch();
    return refetchLegacy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedQuery.isError, isProductDefaultFailed, allProductsQuery.isError, refetchLegacy]);

  return {
    defaultBase: initialSceneData?.base || "000",
    isInitialSceneLoaded,
    isDefaultSceneSettled,
    isInitialSceneReady,
    sceneHasBgTransforms,
    hasRouteDefaultScene,
    hasInitialSceneTransforms,
    initialSceneBgOption,
    // True when an admin override actually applies for the current
    // (channel, product, type, set, layout, bg). useBackgrounds reuses this
    // as its `hasOverrides` so the two hooks can't drift on what counts as
    // override-driven.
    isOverrideDriven: useOverridePath,
    isError: isErrorOverrides || (!useOverridePath && isErrorLegacy),
    error: overridesError ?? (!useOverridePath && isErrorLegacy ? legacyError : null),
    retry,
  };
}
